/**
 * AuditTrailSection - Displays audit history for an investment item
 *
 * Shows a timeline of all changes made to an investment item including:
 * - Creation with reason
 * - Status changes with reasons
 * - Budget changes with reasons
 * - Allocation changes
 */

import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ClockCounterClockwise,
  ArrowRight,
  CurrencyEur,
  Flag,
  Plus,
  User,
  ArrowBendDownRight,
} from "@phosphor-icons/react";
import { useUsers } from "shared/contexts/users.context";
import { getUserName } from "shared/utils/users";
import {
  InvestmentItemAuditLog,
  AuditLogChangeType,
  AUDIT_CHANGE_TYPE_LABELS,
  INVESTMENT_STATUS_LABELS,
} from "../types/investment";

interface AuditTrailSectionProps {
  auditLogs: InvestmentItemAuditLog[];
  isLoading?: boolean;
}

// Format currency helper
const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get icon for change type
const getChangeIcon = (changeType: AuditLogChangeType) => {
  switch (changeType) {
    case AuditLogChangeType.CREATED:
      return <Plus className="h-4 w-4" weight="bold" />;
    case AuditLogChangeType.STATUS_CHANGE:
      return <Flag className="h-4 w-4" weight="bold" />;
    case AuditLogChangeType.BUDGET_CHANGE:
    case AuditLogChangeType.ALLOCATION_CHANGE:
      return <CurrencyEur className="h-4 w-4" weight="bold" />;
    case AuditLogChangeType.BUDGET_TRANSFER:
      return <ArrowBendDownRight className="h-4 w-4" weight="bold" />;
    default:
      return <ClockCounterClockwise className="h-4 w-4" weight="bold" />;
  }
};

// Get color for change type
const getChangeColor = (changeType: AuditLogChangeType) => {
  switch (changeType) {
    case AuditLogChangeType.CREATED:
      return "bg-green-100 text-green-700 border-green-200";
    case AuditLogChangeType.STATUS_CHANGE:
      return "bg-blue-100 text-blue-700 border-blue-200";
    case AuditLogChangeType.BUDGET_CHANGE:
      return "bg-amber-100 text-amber-700 border-amber-200";
    case AuditLogChangeType.ALLOCATION_CHANGE:
      return "bg-moss-primary/10 text-moss-primary border-moss-primary/20";
    case AuditLogChangeType.BUDGET_TRANSFER:
      return "bg-purple-100 text-purple-700 border-purple-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

// Single audit log entry component
const AuditLogEntry = memo(
  ({ log, userName }: { log: InvestmentItemAuditLog; userName: string }) => {
    const renderChangeDetails = () => {
      switch (log.changeType) {
        case AuditLogChangeType.CREATED:
          return (
            <div className="text-sm text-gray-600">
              <span>Initieel budget: </span>
              <span className="font-medium text-gray-900">
                {formatCurrency(log.newValue)}
              </span>
              {log.newStatus && (
                <>
                  <span className="mx-2">|</span>
                  <span>Status: </span>
                  <span className="font-medium text-gray-900">
                    {INVESTMENT_STATUS_LABELS[log.newStatus]}
                  </span>
                </>
              )}
            </div>
          );

        case AuditLogChangeType.STATUS_CHANGE:
          return (
            <div className="flex items-center gap-2 text-sm">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-600">
                {log.oldStatus
                  ? INVESTMENT_STATUS_LABELS[log.oldStatus]
                  : "Geen"}
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <span className="rounded bg-blue-100 px-2 py-0.5 font-medium text-blue-700">
                {log.newStatus
                  ? INVESTMENT_STATUS_LABELS[log.newStatus]
                  : "Geen"}
              </span>
            </div>
          );

        case AuditLogChangeType.BUDGET_CHANGE:
          return (
            <div className="flex items-center gap-2 text-sm">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-600">
                {formatCurrency(log.oldValue)}
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-700">
                {formatCurrency(log.newValue)}
              </span>
            </div>
          );

        case AuditLogChangeType.ALLOCATION_CHANGE:
          return (
            <div className="flex items-center gap-2 text-sm">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-600">
                {formatCurrency(log.oldValue)}
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <span className="rounded bg-moss-primary/10 px-2 py-0.5 font-medium text-moss-primary">
                {formatCurrency(log.newValue)}
              </span>
            </div>
          );

        case AuditLogChangeType.BUDGET_TRANSFER:
          return (
            <div className="text-sm text-gray-600">
              <span className="font-medium text-purple-700">
                {formatCurrency(log.newValue)}
              </span>
              <span> ontvangen van </span>
              {log.sourceItemId ? (
                <Link
                  to={`/investment-lines/${log.sourceItemId}`}
                  className="font-medium text-moss-primary hover:underline"
                >
                  investeringslijn #{log.sourceItemId}
                </Link>
              ) : (
                <span className="text-gray-500">onbekende bron</span>
              )}
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="relative flex gap-4 pb-6 last:pb-0">
        {/* Timeline line */}
        <div className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200 last:hidden" />

        {/* Icon */}
        <div
          className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${getChangeColor(log.changeType)}`}
        >
          {getChangeIcon(log.changeType)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="font-medium text-gray-900">
                {AUDIT_CHANGE_TYPE_LABELS[log.changeType]}
              </span>
              <div className="mt-1">{renderChangeDetails()}</div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-gray-500">
                {formatDate(log.createdAt)}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                <User className="h-3 w-3" />
                {userName}
              </div>
            </div>
          </div>

          {/* Reason */}
          {log.reason && (
            <div className="mt-2 rounded-lg bg-gray-50 p-3">
              <p className="text-sm text-gray-600 italic">"{log.reason}"</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

AuditLogEntry.displayName = "AuditLogEntry";

const AuditTrailSectionComponent = ({
  auditLogs,
  isLoading,
}: AuditTrailSectionProps) => {
  const { users } = useUsers();

  // Sort logs by date descending (newest first)
  const sortedLogs = useMemo(() => {
    return [...auditLogs].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [auditLogs]);

  // Get user names for all logs
  const getUserNameForLog = (userId: string) => {
    if (!userId) return "Systeem";
    const userData = getUserName(userId, users);
    return userData?.name || "Onbekende gebruiker";
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockCounterClockwise className="h-5 w-5 text-gray-400" />
          <h3 className="font-semibold text-gray-900">Audit Trail</h3>
        </div>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="h-8 w-8 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/4 rounded bg-gray-200" />
                <div className="h-3 w-1/2 rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockCounterClockwise className="h-5 w-5 text-gray-400" />
          <h3 className="font-semibold text-gray-900">Audit Trail</h3>
        </div>
        <p className="text-sm text-gray-500">
          Geen wijzigingsgeschiedenis beschikbaar.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="flex items-center gap-2 mb-6">
        <ClockCounterClockwise className="h-5 w-5 text-gray-400" />
        <h3 className="font-semibold text-gray-900">Audit Trail</h3>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {auditLogs.length} {auditLogs.length === 1 ? "wijziging" : "wijzigingen"}
        </span>
      </div>

      <div className="space-y-0">
        {sortedLogs.map((log) => (
          <AuditLogEntry
            key={log.id}
            log={log}
            userName={getUserNameForLog(log.changedByUserId)}
          />
        ))}
      </div>
    </div>
  );
};

export const AuditTrailSection = memo(AuditTrailSectionComponent);
export default AuditTrailSection;
